import React, { useState, useEffect, useContext } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { compose } from '../../utils/compose';
import { withFirebase } from '../Firebase';
import { AuthUserContext, withAuthorization } from '../Session';
import * as ROUTES from '../../constants/routes';
import { FRANCHISE_FEE, getRazorpayOptions } from '../../utils/razorpayConfig';
import { useAppAlert } from '../AppAlertProvider';
import { createFranchiseAgreementPdfDocument } from '../../utils/franchiseAgreementPdf';
// eStampConfig removed - e-stamp is now triggered server-side after payment
import './styles.css';

const FranchiseAgreementBase = ({ firebase }) => {
  const { agreementId } = useParams();
  const history = useHistory();
  const authUser = useContext(AuthUserContext);
  const { showAlert } = useAppAlert();
  const [agreement, setAgreement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [error, setError] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [existingPayment, setExistingPayment] = useState(null);
  const [razorpayReady, setRazorpayReady] = useState(
    typeof window !== 'undefined' && !!window.Razorpay
  );
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [editOriginal, setEditOriginal] = useState(null);
  const [savingEdits, setSavingEdits] = useState(false);
  const [retryingEstamp, setRetryingEstamp] = useState(false);
  const previousAgreementIdRef = React.useRef(null);
  const previousPaymentCompletedRef = React.useRef(null);
  const showingPostPaymentModalRef = React.useRef(false);
  const stampStatus = String(
    agreement?.stampnsignStatus || agreement?.eStampSignStatus || agreement?.status || '',
  ).toLowerCase();
  const isEsigned = ['esigned', 'estamp_esigned', 'estamp_completed'].includes(stampStatus);
  const isLinkedFromPreviousPayment = Boolean(agreement?.paymentLinkedFromPrevious);
  const showRetryEstampButton = Boolean(
    agreement?.paymentCompleted && isLinkedFromPreviousPayment,
  );

  useEffect(() => {
    if (!authUser || !agreementId) {
      setLoading(false);
      return;
    }

    const unsubscribe = firebase.db
      .collection('franchiseAgreements')
      .doc(agreementId)
      .onSnapshot(
        (doc) => {
          if (doc.exists) {
            const data = doc.data();
            
            // Check if agreement belongs to current user OR if user is admin
            const isAdmin = authUser.email === 'jeethbuildcon@gmail.com';
            if (data.userId !== authUser.uid && !isAdmin) {
              setError('Unauthorized: This agreement does not belong to you.');
              setLoading(false);
              return;
            }

            // Check if expired (skip for admin or if payment already completed)
            const expiresAt = data.expiresAt
              ? data.expiresAt.toDate()
              : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000); // 7-day expiry by default
            const isExpired = expiresAt && new Date() > expiresAt;
            if (isExpired && !isAdmin && !data.paymentCompleted) {
              setError('EXPIRED_UNPAID');
              setLoading(false);
              return;
            }

            setAgreement({ id: doc.id, ...data });
            setAgreedToTerms(data.agreedToTerms || false);
            setLoading(false);
          } else {
            setError('Agreement not found.');
            setLoading(false);
          }
        },
        (err) => {
          console.error('Error loading agreement:', err);
          setError('Failed to load agreement: ' + err.message);
          setLoading(false);
        }
      );

    return () => unsubscribe();
  }, [authUser, agreementId, firebase]);

  const handleGoToDashboard = () => {
    history.push({
      pathname: ROUTES.DASHBOARD,
      search: '?view=franchise',
    });
  };

  // Show the same success modal when payment flips from unpaid -> paid in real time
  // (e.g., manual cash script updates while this page is open).
  useEffect(() => {
    if (!agreement?.id) return;

    const isAdminUser = (authUser?.email || '').toLowerCase() === 'jeethbuildcon@gmail.com';
    const isPaidNow = Boolean(agreement.paymentCompleted);
    const isLinkedFromPrevious = Boolean(agreement.paymentLinkedFromPrevious);

    if (previousAgreementIdRef.current !== agreement.id) {
      previousAgreementIdRef.current = agreement.id;
      previousPaymentCompletedRef.current = isPaidNow;
      return;
    }

    const shouldShowSuccessModal =
      previousPaymentCompletedRef.current === false &&
      isPaidNow &&
      !isLinkedFromPrevious &&
      !isAdminUser &&
      !showingPostPaymentModalRef.current;

    previousPaymentCompletedRef.current = isPaidNow;
    if (!shouldShowSuccessModal) return;

    showingPostPaymentModalRef.current = true;
    (async () => {
      const action = await showAlert({
        title: 'Successful',
        message: 'Click proceed to go to the franchise dashboard.',
        confirmText: 'Proceed',
        variant: 'success',
      });

      if (action === 'confirmed') {
        handleGoToDashboard();
      }
    })().finally(() => {
      showingPostPaymentModalRef.current = false;
    });
  }, [
    agreement?.id,
    agreement?.paymentCompleted,
    agreement?.paymentLinkedFromPrevious,
    authUser?.email,
    showAlert,
    history,
  ]);

  // Keep editable form data in sync with the agreement
  useEffect(() => {
    if (agreement?.formData) {
      setEditForm(agreement.formData);
      setEditOriginal(agreement.formData);
    }
  }, [agreement]);

  // Check for existing payment record
  useEffect(() => {
    if (!authUser) return;

    const checkExistingPayment = async () => {
      try {
        const paymentDoc = await firebase.db
          .collection('payments')
          .doc(authUser.uid)
          .get();

        if (paymentDoc.exists) {
          const paymentData = paymentDoc.data();
          setExistingPayment(paymentData);
          
          // If agreement doesn't have payment info but user has paid, link it
          if (agreement && !agreement.paymentCompleted) {
            await firebase.db
              .collection('franchiseAgreements')
              .doc(agreementId)
              .update({
                paymentCompleted: true,
                paymentId: paymentData.paymentId,
                paymentAmount: paymentData.amount,
                linkedPaymentId: paymentData.paymentId,
                skipPayment: true,
                paymentLinkedFromPrevious: true,
                paymentMethod: paymentData.method,
                paymentCompletedAt: paymentData.completedAt,
                expiresAt: null,
                status: 'payment_completed',
              });
            
            console.log('[OK] Existing payment linked to agreement:', paymentData.paymentId);
          }
        }
      } catch (err) {
        console.error('Error checking existing payment:', err);
      }
    };

    checkExistingPayment();
  }, [authUser, agreement, agreementId, firebase]);

  // Load Razorpay checkout script once
  useEffect(() => {
    if (typeof window === 'undefined' || razorpayReady) {
      return;
    }

    const scriptUrl = 'https://checkout.razorpay.com/v1/checkout.js';
    const existingScript = document.querySelector(`script[src="${scriptUrl}"]`);

    if (existingScript) {
      existingScript.addEventListener('load', () => setRazorpayReady(true), { once: true });
      existingScript.addEventListener(
        'error',
        () => {
          console.error('Razorpay checkout script failed to load.');
          setRazorpayReady(false);
        },
        { once: true }
      );
      return;
    }

    const script = document.createElement('script');
    script.src = scriptUrl;
    script.async = true;
    script.onload = () => setRazorpayReady(true);
    script.onerror = () => {
      console.error('Razorpay checkout script failed to load.');
      setRazorpayReady(false);
    };
    document.body.appendChild(script);
  }, [razorpayReady]);

  const handleAgreeChange = async (e) => {
    const checked = e.target.checked;
    setAgreedToTerms(checked);

    try {
      await firebase.db
        .collection('franchiseAgreements')
        .doc(agreementId)
        .update({
          agreedToTerms: checked,
          agreedAt: checked ? firebase.fieldValue.serverTimestamp() : null,
        });
    } catch (err) {
      console.error('Error updating agreement:', err);
      await showAlert({
        title: 'Error',
        message: err.message || 'Failed to update agreement.',
        variant: 'error',
      });
    }
  };

  const handleOpenEdit = () => {
    if (agreement?.paymentCompleted) return;
    setEditOriginal(agreement?.formData || {});
    setEditForm(agreement?.formData || {});
    setShowEditModal(true);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => {
      const next = {
        ...(prev || {}),
        [name]: value,
      };

      if (name === 'holdActiveRera' && value === 'yes') {
        next.arrangeReraIn90Days = 'no';
      }

      if (name === 'arrangeReraIn90Days' && value === 'yes') {
        next.holdActiveRera = 'no';
      }

      if (next.holdActiveRera !== 'yes') {
        next.reraNumber = '';
      }

      return next;
    });
  };

  const handleSaveEdits = async () => {
    if (!agreement || !editForm) {
      return;
    }

    if (agreement.paymentCompleted) {
      await showAlert({
        title: 'Payment Locked',
        message: 'Details cannot be edited after payment is completed.',
        variant: 'warning',
      });
      return;
    }

    const hasActiveRera = String(editForm.holdActiveRera || '').toLowerCase() === 'yes';
    const canArrangeReraIn90Days = String(editForm.arrangeReraIn90Days || '').toLowerCase() === 'yes';
    const normalizedEditForm = {
      ...editForm,
      reraNumber: hasActiveRera ? String(editForm.reraNumber || '').trim() : '',
    };

    if (!hasActiveRera && !canArrangeReraIn90Days) {
      await showAlert({
        title: 'RERA Information Required',
        message: 'Please select "Yes" for either "Do You Hold Active RERA?" or "Can You Arrange For RERA in 90 Days?".',
        variant: 'warning',
      });
      return;
    }

    if (hasActiveRera && !normalizedEditForm.reraNumber) {
      await showAlert({
        title: 'RERA Number Required',
        message: 'Please enter your active RERA registration number.',
        variant: 'warning',
      });
      return;
    }

    setSavingEdits(true);
    try {
      await firebase.db.collection('franchiseAgreements').doc(agreementId).update({
        formData: normalizedEditForm,
        status: agreement.status === 'payment_completed' ? agreement.status : 'pending_agreement',
        updatedAt: firebase.fieldValue.serverTimestamp(),
      });

      setEditOriginal(normalizedEditForm);
      setShowEditModal(false);
      await showAlert({
        title: 'Details Updated',
        message: 'Your details have been updated in the agreement.',
        variant: 'success',
      });
    } catch (err) {
      console.error('Error updating agreement details:', err);
      await showAlert({
        title: 'Update Failed',
        message: err.message || 'Unable to update details. Please try again.',
        variant: 'error',
      });
    } finally {
      setSavingEdits(false);
    }
  };

  const hasUnsavedChanges = () => {
    try {
      return JSON.stringify(editForm || {}) !== JSON.stringify(editOriginal || {});
    } catch {
      return false;
    }
  };

  const handleCloseEdit = async () => {
    if (savingEdits) return;
    if (hasUnsavedChanges()) {
      const action = await showAlert({
        title: 'Save changes?',
        message: 'You have unsaved changes. Save before closing?',
        variant: 'warning',
        confirmText: 'Save & Close',
        cancelText: 'Discard',
      });
      if (action === 'confirmed') {
        await handleSaveEdits();
        return;
      }
    }
    setShowEditModal(false);
    setEditForm(editOriginal);
  };

  const handleCancelEdit = () => {
    if (savingEdits) return;
    setShowEditModal(false);
    setEditForm(editOriginal);
  };

  const handleDownloadPDF = () => {
    if (!agreement || !agreement.formData) {
      showAlert({
        title: 'Agreement Not Ready',
        message: 'Please wait until the agreement loads before downloading.',
        variant: 'warning',
      });
      return;
    }

    const { formData } = agreement;

    try {
      const createdAt = resolveAgreementDate(agreement);
      const doc = createFranchiseAgreementPdfDocument(formData, createdAt ? { createdAt } : {});
      const fileName = `Franchise_Agreement_${formData.fullName?.replace(/\s+/g, '_') || 'Document'}_${Date.now()}.pdf`;
      doc.save(fileName);
    } catch (error) {
      console.error('Error generating PDF:', error);
      showAlert({
        title: 'Download Failed',
        message: 'Something went wrong while generating the PDF. Please try again.',
        variant: 'error',
      });
    }
  };

  const verifyRazorpayPayment = async (paymentResponse) => {
    try {
      const verifyPayment = firebase.functions.httpsCallable('verifyRazorpayPayment');
      const verification = await verifyPayment({
        agreementId,
        razorpayPaymentId: paymentResponse.razorpay_payment_id,
        razorpayOrderId: paymentResponse.razorpay_order_id,
        razorpaySignature: paymentResponse.razorpay_signature,
      });

      if (!verification.data?.success) {
        throw new Error(verification.data?.error || 'Payment verification failed');
      }

      // Clear expiry once payment is successful
      try {
        await firebase.db.collection('franchiseAgreements').doc(agreementId).update({
          expiresAt: null,
        });
      } catch (err) {
        console.warn('Could not clear expiry after payment:', err);
      }

      // E-stamp & e-sign is now triggered automatically from the server-side
      // after payment verification in Cloud Functions (recordSuccessfulPayment)
      // No client-side action needed here

      setProcessingPayment(false);
      history.push(ROUTES.PAYMENT_SUCCESS.replace(':agreementId', agreementId));
    } catch (err) {
      console.error('Error verifying Razorpay payment:', err);
      setProcessingPayment(false);
      await showAlert({
        title: 'Payment Verification Failed',
        message: err.message
          ? err.message
          : 'Please contact support with your payment ID.',
        variant: 'error',
      });
    }
  };

  const handlePayment = async () => {
    if (!agreement || !agreedToTerms) {
      showAlert({
        title: 'Action Needed',
        message: 'Please agree to the terms before proceeding.',
        variant: 'warning',
      });
      return;
    }

    if (agreement.paymentCompleted) {
      showAlert({
        title: 'Payment Already Completed',
        message: 'This agreement has already been paid for.',
        variant: 'info',
      });
      return;
    }

    if (typeof window === 'undefined' || !window.Razorpay || !razorpayReady) {
      showAlert({
        title: 'Payment Gateway Loading',
        message: 'Please wait a moment and try again.',
        variant: 'info',
      });
      return;
    }

    setProcessingPayment(true);

    try {
      const customerName = agreement.formData?.fullName || authUser.displayName || 'Customer';
      const customerEmail = agreement.formData?.email || authUser.email;
      const customerPhone = agreement.formData?.phone || '';

      const createOrder = firebase.functions.httpsCallable('createRazorpayOrder');
      const result = await createOrder({
        agreementId,
        amount: FRANCHISE_FEE.totalAmount,
        customerName,
        customerEmail,
        customerPhone,
      });

      if (!result.data?.success) {
        throw new Error(result.data?.error || 'Failed to initiate payment');
      }

      const { orderId, amount, currency, keyId } = result.data;

      const options = getRazorpayOptions({
        keyId,
        orderId,
        amount,
        currency,
        customerName,
        customerEmail,
        customerPhone,
        onSuccess: verifyRazorpayPayment,
        onFailure: async (failure) => {
          setProcessingPayment(false);
          const message = failure?.reason || 'Payment cancelled. Please try again when you are ready.';
          await showAlert({
            title: 'Payment Cancelled',
            message,
            variant: 'error',
          });
        },
      });

      const razorpayCheckout = new window.Razorpay(options);

      razorpayCheckout.on('payment.failed', async (response) => {
        console.error('Razorpay payment failed:', response);
        setProcessingPayment(false);
        const message =
          response?.error?.description ||
          response?.error?.reason ||
          'Payment failed. Please try again.';
        await showAlert({
          title: 'Payment Failed',
          message,
          variant: 'error',
        });
      });

      razorpayCheckout.open();
    } catch (err) {
      console.error('Error initiating Razorpay payment:', err);
      await showAlert({
        title: 'Payment Error',
        message: err.message || 'Failed to initiate payment. Please try again.',
        variant: 'error',
      });
      setProcessingPayment(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '___';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const resolveAgreementDate = (agreementData) => {
    const candidate =
      agreementData?.createdAt ||
      agreementData?.formData?.agreementCreatedAt ||
      agreementData?.formData?.createdAt;
    if (!candidate) return null;
    if (candidate instanceof Date) return candidate;
    if (typeof candidate.toDate === 'function') return candidate.toDate();
    if (typeof candidate.seconds === 'number') return new Date(candidate.seconds * 1000);
    const parsed = new Date(candidate);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  if (loading) {
    return (
      <div className="agreement-container loading">
        <div className="spinner"></div>
        <p>Loading agreement...</p>
      </div>
    );
  }

  if (error) {
    if (error === 'EXPIRED_UNPAID') {
      return (
        <div className="agreement-container error">
          <h2>Agreement Expired</h2>
          <p>Your agreement expired before payment. Please fill the franchise form to generate a new agreement.</p>
          <button onClick={() => history.push(ROUTES.HOME + '?view=franchise')}>
            Open Franchise Form
          </button>
        </div>
      );
    }
    return (
      <div className="agreement-container error">
        <h2>Error</h2>
        <p>{error}</p>
        <button onClick={() => history.push(ROUTES.DASHBOARD)}>Go Back to Dashboard</button>
      </div>
    );
  }

  if (!agreement) {
    return null;
  }

  const { formData } = agreement;
  const agreementDate = resolveAgreementDate(agreement);
  const day = agreementDate ? agreementDate.getDate() : '___';
  const month = agreementDate ? agreementDate.toLocaleDateString('en-US', { month: 'long' }) : '___';
  const year = agreementDate ? agreementDate.getFullYear() : '___';
  const isAdmin = authUser.email === 'jeethbuildcon@gmail.com';
  const hasActiveRera = String(formData.holdActiveRera || '').toLowerCase() === 'yes';
  const hasLegacyReraDetails =
    !formData.holdActiveRera &&
    !formData.arrangeReraIn90Days &&
    Boolean(String(formData.reraNumber || '').trim());
  const shouldShowReraClause = hasActiveRera || hasLegacyReraDetails;
  const editHasActiveRera = String(editForm?.holdActiveRera || '').toLowerCase() === 'yes';
  const editCanArrangeReraIn90Days = String(editForm?.arrangeReraIn90Days || '').toLowerCase() === 'yes';

  return (
    <div className="agreement-container">
      <div className="agreement-header">
        <h1>Franchise Agreement</h1>
        {isAdmin && (
          <div style={{ 
            background: 'linear-gradient(135deg, #d4af37 0%, #e4c568 100%)', 
            padding: '8px 16px', 
            borderRadius: '8px', 
            color: '#0c1c2d',
            fontWeight: 'bold',
            fontSize: '14px'
          }}>
            Admin View Mode
          </div>
        )}
        <div style={{ display: 'flex', gap: '12px' }}>
          <button onClick={handleOpenEdit} className="btn-download-pdf">
            <span>Edit Details</span>
          </button>
          <button onClick={handleDownloadPDF} className="btn-download-pdf">
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span>Download PDF</span>
          </button>
        </div>
      </div>

      <div className="agreement-document">
        <h2 className="doc-title">Franchise Agreement</h2>

        <p className="agreement-text">
          This Franchise Agreement ("Agreement") is made and entered into on this <strong>{day}</strong> day of <strong>{month}</strong>, <strong>{year}</strong>
        </p>

        <h3>BY AND BETWEEN:</h3>

        <p className="agreement-text">
          <strong>Steadeesoft AI Technologies Pvt Ltd</strong>, a company incorporated under the Companies Act, 2013, having its registered office at No 74-1-3363/a-4-13, HAL 2nd Stage, Bengaluru, 560008, Bangalore, Karnataka, India (hereinafter referred to as the "Franchisor", which term shall include its successors and assigns),
        </p>

        <p className="agreement-text">
          <strong>AND</strong>
        </p>

        <p className="agreement-text">
          <strong>{formData.fullName || '[Insert Franchisee Name]'}</strong>, {formData.entityType === 'individual' ? 'an individual' : 'an entity'} having {formData.entityType === 'individual' ? 'residence' : 'its registered office'} at <strong>{formData.addressLine1 || '[Address]'}, {formData.addressLine2 ? formData.addressLine2 + ', ' : ''}{formData.city || '[City]'}, {formData.state || '[State]'} - {formData.pincode || '[Pincode]'}, India</strong>
          {shouldShowReraClause ? (
            <>
              , holding a valid RERA registration number <strong>{formData.reraNumber || '_____________'}</strong>
            </>
          ) : null}
          , (hereinafter referred to as the "Franchisee", which term shall include its successors, permitted assigns and legal representatives).
        </p>

        <h3>WHEREAS:</h3>

        <h3>DEFINITIONS</h3>
        
        <p className="agreement-text">
          Unless otherwise stated, the following terms shall have the meanings ascribed below:
        </p>

        <p className="agreement-text">
          <strong>1.1</strong> "Brand" means Douglas & McCartney, owned by the Brand Licensor and licensed to the Franchisor.
        </p>

        <p className="agreement-text">
          <strong>1.2</strong> "Franchise Fee" means a non refundable fee of Rs. 1,00,000 plus GST of Rs. 18,000 (total Rs. 1,18,000) payable by the Franchisee.
        </p>

        <p className="agreement-text">
          <strong>1.3</strong> "Territory" means the Republic of India.
        </p>

        <p className="agreement-text">
          <strong>1.4</strong> "Effective Date" means the date of execution of this Agreement.
        </p>

        <p className="agreement-text">
          <strong>1.5</strong> "Franchise Term" means 5 (five) years from the Effective Date.
        </p>

        <p className="agreement-text">
          <strong>1.6</strong> "System" includes methods of operation, marketing techniques, training materials, and other intellectual property associated with the Brand
        </p>

        <div className="agreement-section">
          <p className="agreement-text">
            <strong>A.</strong> The brand name Douglas & McCartney TM (applied) is licensed to the Franchisor by Steadee Soft AI Technologies LLC FZ, a company registered in Dubai, UAE (hereinafter referred to as the "Brand Licensor"), and is used in connection with real estate software & business services;
          </p>

          <p className="agreement-text">
            <strong>B.</strong> The Franchisor has developed a system, trade name, branding, and operational framework associated with real estate services under the Douglas & McCartney brand;
          </p>

          <p className="agreement-text">
            <strong>C.</strong> The Franchisee is desirous of conducting real estate business in India using the Franchisor's brand, tools, and system, and maintaining their own independent RERA license and legal entity;
          </p>

          <p className="agreement-text">
            <strong>D.</strong> The Franchisor is willing to grant a non-exclusive, non-transferable, limited franchise for a term of 5 (five) years in accordance with the terms set out herein.
          </p>
        </div>

        <h3>GRANT OF FRANCHISE</h3>
        
        <p className="agreement-text">
          <strong>2.1 License Grant:</strong> The Franchisor hereby grants to the Franchisee a limited, non-exclusive, non-transferable, non-sublicensable right to operate a real estate business under the Douglas & McCartney brand within India for the Franchise Term.
        </p>

        <p className="agreement-text">
          <strong>2.2 Scope of Use:</strong> The Franchisee shall operate independently using its own legal entity, RERA registration, and billing mechanisms, and shall not hold itself out as an agent or employee of the Franchisor. In case the Franchisee does not have an active RERA registration, the Franchisee shall endeavor to become compliant within 90 days of signing this document. If this compliance is not met, the Franchisor can and will terminate this Agreement by giving prior written notice. No refund would be applicable.
        </p>

        <h3>RIGHTS, TERM AND FEES</h3>

        <h3>1. THE PREAMBLE</h3>
        <p className="agreement-text">
          Douglas & Mccartney is the intellectual property of SteadeeSoft AI Technologies pvt ltd which holds all the intellectual property rights like trade mark, design, name, etc
        </p>

        <p className="agreement-text">
          The franchisor is engaged in the business of real estate education & training pertaining to enabling the individuals to upscale their skills to become successful in their endeavours of real estate consulting entrepreneurship. The franchisor enables the franchisee to plug into the eco system of branding, training and networking in real estate consulting in accordance with a distinctive system and standard operating procedures, utilizing and comprising certain proprietary marks and confidential information.
        </p>

        <p className="agreement-text">
          The Franchisee desires to obtain the benefit of Douglas & McCartney's goodwill, intellectual property and operations know-how, for the purpose of operating an efficient real estate consulting business
        </p>

        <p className="agreement-text">
          The Franchisor through its own effort has established substantial reputation and goodwill in the Business and the Franchisee recognizes the benefit to be derived there from and acknowledges the necessity of conforming to the high standards and uniform specifications of the Business.
        </p>

        <h3>2. FRANCHISE RIGHTS</h3>
        <p className="agreement-text">
          During the tenure of the Franchise Agreement, the Franchisee is granted.
        </p>
        <p className="agreement-text">
          <strong>a)</strong> The exclusive rights to operate in the field of real estate consulting using the brand name, Douglas & McCartney
        </p>
        <p className="agreement-text">
          <strong>b)</strong> The exclusive right to use the Franchisor's or its related entity's intellectual property and operations know how including but not limited to its trade name, marks and copyrights, strictly for the conduct of the Business.
        </p>

        <h3>3. TERM AND RENEWAL</h3>
        <p className="agreement-text">
          <strong>3.1</strong> This Agreement shall remain in force for a period of 5 (five) years from the Effective Date.
        </p>
        <p className="agreement-text">
          <strong>3.2</strong> The Agreement may be renewed for additional terms upon mutual agreement separately drawn in writing, subject to terms and payment of prevailing applicable fees at the time of renewal
        </p>

        <h3>4. INITIAL FRANCHISE FEE</h3>
        <p className="agreement-text">
          The franchise fee payable by the Franchisee shall comprise of an up-front, non refundable amount of INR 1,00,000 plus GST of INR 18,000 (18%), totaling INR 1,18,000,
        </p>

        <h3>5. ONGOING FEES/ROYALTY</h3>
        <p className="agreement-text">
          The Franchisee shall pay to the Franchisor 0% (ZERO %) of its monthly network sales revenue
        </p>

        <h3>6. FRANCHISOR OBLIGATIONS</h3>
        <p className="agreement-text">The Franchisor shall:</p>
        <ul className="agreement-list">
          <li>allow use of brand name for conducting real estate business.</li>
          <li>Provide access to online training via scheduled video conferencing;</li>
          <li>Allow the use of an official Douglas & McCartney email address;</li>
          <li>Offer branding guidelines, templates, and operational assistance as may be reasonably required.</li>
          <li>Offer branded merchandise, customised merchandise, business card templates, early access to mandate projects, reading material,</li>
        </ul>

        <h3>7. FRANCHISEE OBLIGATIONS</h3>
        <p className="agreement-text"><strong>7.1</strong> The Franchisee shall:</p>
        <ul className="agreement-list">
          <li>Maintain a valid RERA registration for the duration of this Agreement;</li>
          <li>Conduct business in a professional and ethical manner;</li>
          <li>Use the Brand solely for real estate activities;</li>
          <li>maintain confidentiality of the training provided safe guarding intellectual property rights</li>
          <li>Comply with all applicable laws and regulations;</li>
          <li>Keep all customer and transactional records confidential.</li>
        </ul>
        <p className="agreement-text">
          <strong>7.2</strong> The Franchisee shall be solely responsible for its own taxes, employment matters, regulatory filings, and all operational liabilities.
        </p>
        <p className="agreement-text">
          <strong>7.3</strong> The Franchisor shall not be liable for the Franchisee's operations, representations, or third-party claims arising out of the Franchisee's independent activities.
        </p>

        <h3>8. TERMINATION</h3>
        <p className="agreement-text">
          The Franchisor may terminate the franchisee agreement for any material breach committed by the franchisee by providing a show cause notice of 15 days for which the franchisor provides the curative remedy rectifying the breach or provide sufficient cause for such lapse that has transpired to a breach within 15 days of such communication.
        </p>
        <p className="agreement-text">
          If the franchisee has failed to comply to the statutory requirements of the government as per the law prevailing from time to time.
        </p>
        <p className="agreement-text">
          The Franchisor may forthwith terminate the Franchisee if he is convicted for any criminal offence and is in the opinion of the Franchisor guilty of gross moral turpitude or crime of financial nature
        </p>
        <p className="agreement-text">
          Any action detrimental to the interests of the brand shall invoke instant termination subject to the show cause provision provided in this agreement.
        </p>

        <h3>9. CONSEQUENCES TO TERMINATION</h3>
        <p className="agreement-text">
          The franchisee ceases to use or transact the proprietary intellectual property including the material provided by the franchisor and shall discontinue to transact in the name of the franchisor.
        </p>
        <p className="agreement-text">
          Indemnify the Franchisor of any such damage or damages that may be caused due to the mis act of the franchisee not limiting to himself/herself or he being vicariously liable for the conduct of his agents and employees
        </p>

        <h3>10. PROPRIETARY MARKS</h3>
        <p className="agreement-text">
          The Franchisor or its related company will be responsible for the protection of the Douglas & McCartney TM (applied) trademarks. The Franchisor shall grant to the Franchisee a royalty-free, non-assignable right to use the Douglas & McCartney trademark for use in relation to the Franchise, which right shall be exclusive for real estate.
        </p>
        <p className="agreement-text">
          The Franchisee acknowledges that these marks belong to the Franchisor or its related company, and will not willfully infringe them, register ownership of them, or contest the ownership and rights of the Franchisor or its related company. The Franchisee will give its fullest cooperation in protecting the Douglas & McCartney trademarks, intellectual property, and goodwill.
        </p>

        <h3>11. NON-COMPETITION</h3>
        <p className="agreement-text">
          The Franchisee and his/her immediate families shall not engage directly or indirectly in any activity that may be prejudicial or competes/imitates the franchisor's business model, during the term of the Franchise Agreement and for five (5) years thereafter.
        </p>

        <h3>12. CONFIDENTIALITY</h3>
        <p className="agreement-text">
          <strong>12.1</strong> Not to reveal to external parties confidential information including but not limited to those contained in the operations documentation, business plans, internal instruction circulars, correspondence and any other materials marked as confidential.
        </p>
        <p className="agreement-text">
          <strong>12.2</strong> Each party agrees to maintain in strict confidence any confidential information received during the course of this Agreement.
        </p>
        <p className="agreement-text">
          <strong>12.3</strong> This obligation shall survive for 2 (two) years post-termination of the Agreement
        </p>

        <h3>13. INTELLECTUAL PROPERTY</h3>
        <p className="agreement-text">
          <strong>13.1</strong> The Franchisee acknowledges that all intellectual property rights in the Brand and System are owned by the Franchisor.
        </p>
        <p className="agreement-text">
          <strong>13.2</strong> The Franchisee shall not use the Brand or any derivative thereof except as permitted under this Agreement.
        </p>
        <p className="agreement-text">
          <strong>13.3</strong> Upon termination or expiration, the Franchisee shall immediately cease using the Brand and related materials
        </p>

        <h3>14. GOVERNMENT REGULATIONS</h3>
        <p className="agreement-text">
          To comply with all government regulations and licensing requirements governing the conduct of operations.
        </p>

        <h3>15. RENEWAL</h3>
        <p className="agreement-text">
          To notify the Franchisor in writing not less than three (3) months before the expiration of the initial franchise term if it intends to exercise the option for extension of the term. The applicable cost of renewal is subjective, at the discretion of the Franchisor, the franchisee having complied to the stipulated agreements and guidelines laid upon by the Franchisor without any breach or deviations and will be extended upon mutual terms under a separate agreement.
        </p>

        <h3>16. OTHER TERMS</h3>
        <h4>LAW AND ARBITRATION</h4>
        <p className="agreement-text">
          All rights and obligations of the parties under this Franchise Agreement shall be governed and construed in accordance with the laws of India.
        </p>
        <p className="agreement-text">
          Arbitration in Bangalore shall be the forum to resolve disputes, with a single arbitrator being appointed by the parties mutually.
        </p>

        <p className="agreement-text" style={{ marginTop: '2rem', fontWeight: 'bold' }}>
          IN WITNESS WHEREOF, the parties have duly executed these Heads of Agreement as of the date set forth above.
        </p>

        <div className="signature-section">
          <div className="signature-block">
            <p><strong>SIGNED By:</strong></p>
            <p><strong>__________________________________)</strong></p>
            <p>For and on behalf of</p>
            <p><strong>STEADEE Soft AI Technologies Pvt Ltd</strong></p>
          </div>

          <div className="signature-block">
            <p><strong>SIGNED by:</strong></p>
            <p><strong>{formData.fullName || '____________________'}</strong></p>
            <p>__________________________________)</p>
          </div>
        </div>
      </div>

      {/* Terms Acceptance & Payment - Hide for admin */}
      {!isAdmin && (
        <div className="agreement-actions">
          {agreement && !agreement.paymentCompleted && (
            <div className="edit-card">
              <div className="edit-card__header">
                <div className="edit-card__title">Edit your details before paying</div>
                <button className="edit-card__btn" onClick={handleOpenEdit}>
                  Edit Details
                </button>
              </div>
              <div className="edit-card__sub">Changes here will update the agreement fields before payment. You cannot edit details after payment is completed.</div>
            </div>
          )}
          {/* Payment Summary */}
          {agreement && !agreement.paymentCompleted && (
            <div className="payment-summary sleek-panel">
              <div className="payment-header">Franchise Fee Payment</div>
              <div className="payment-breakdown">
                <div className="payment-row">
                  <span>Base Franchise Fee:</span>
                  <strong>Rs. {FRANCHISE_FEE.baseAmount.toLocaleString('en-IN')}</strong>
                </div>
                <div className="payment-row">
                  <span>GST (18%):</span>
                  <strong>Rs. {FRANCHISE_FEE.gst.toLocaleString('en-IN')}</strong>
                </div>
                <div className="payment-total">
                  <span>Total Amount:</span>
                  <span>Rs. {FRANCHISE_FEE.totalAmount.toLocaleString('en-IN')}</span>
                </div>
              </div>
            </div>
          )}

          {/* Payment Completed Message */}
          {agreement && (agreement.paymentCompleted || existingPayment) && (
            <div style={{
              background: 'rgba(40, 167, 69, 0.1)',
              border: '2px solid #28a745',
              borderRadius: '12px',
              padding: '24px',
              marginBottom: '24px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>PAID</div>
              <h3 style={{ margin: '0 0 8px 0', color: '#28a745', fontSize: '20px' }}>
                Payment Already Received!
              </h3>
              <p style={{ margin: 0, color: '#6c757d', fontSize: '14px' }}>
                {isLinkedFromPreviousPayment || (existingPayment && !agreement.paymentCompleted)
                  ? 'Your previous payment has been linked to this application. No additional payment required.'
                  : 'Thank you for your payment. Awaiting admin approval.'}
              </p>
              {(agreement.paymentId || existingPayment?.paymentId) && (
                <>
                  <p style={{ margin: '12px 0 0 0', color: '#6c757d', fontSize: '12px' }}>
                    Payment ID: <strong>{agreement.paymentId || existingPayment.paymentId}</strong>
                  </p>
                  {existingPayment && (
                    <p style={{ margin: '8px 0 0 0', color: '#6c757d', fontSize: '12px' }}>
                      Amount: <strong>Rs. {(existingPayment.amount || FRANCHISE_FEE.totalAmount).toLocaleString('en-IN')}</strong>
                    </p>
                  )}
                </>
              )}
              {showRetryEstampButton && (
                  <button
                    style={{
                      marginTop: '16px',
                      padding: '12px 18px',
                      borderRadius: '8px',
                      border: 'none',
                      background: '#d4af37',
                      color: '#0c1c2d',
                      fontWeight: 700,
                      cursor: retryingEstamp || isEsigned ? 'not-allowed' : 'pointer',
                      opacity: retryingEstamp || isEsigned ? 0.6 : 1,
                    }}
                    disabled={retryingEstamp || isEsigned}
                    onClick={async () => {
                      try {
                        if (retryingEstamp || isEsigned) return;
                        setRetryingEstamp(true);
                        const retryEStamp = firebase.functions.httpsCallable('retryEStamp');
                        await retryEStamp({ agreementId: agreement.id });
                        const action = await showAlert({
                          title: 'Successful',
                          message: 'Your agreement has been sent for e-signing and e-stamping. Click proceed to go to the franchise dashboard.',
                          confirmText: 'Proceed',
                          variant: 'success',
                        });
                        if (action === 'confirmed') {
                          handleGoToDashboard();
                        }
                    } catch (err) {
                      console.error('Retry e-stamp failed', err);
                      await showAlert({
                        title: 'Retry Failed',
                        message: err.message || 'Could not re-submit. Please try again.',
                        variant: 'error',
                      });
                    } finally {
                      setRetryingEstamp(false);
                    }
                  }}
                >
                  {retryingEstamp ? 'Submitting...' : 'Submit again for E-sign & E-stamp'}
                </button>
              )}
            </div>
          )}

          <div className="terms-checkbox">
            <label>
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={handleAgreeChange}
                disabled={agreement && agreement.paymentCompleted}
              />
              <span>I have read and agree to the terms and conditions of this Franchise Agreement</span>
            </label>
          </div>

          {processingPayment && (
            <div
              style={{
                background: '#e8f4f8',
                border: '2px solid #0c1c2d',
                borderRadius: '8px',
                padding: '20px',
                marginBottom: '20px',
                textAlign: 'center',
              }}
            >
              <div style={{ marginBottom: '15px', fontSize: '32px' }}>Processing Payment</div>
              <h3 style={{ margin: '0 0 10px 0', color: '#0c1c2d', fontSize: '18px' }}>
                Complete Payment in Razorpay Window
              </h3>
              <p style={{ margin: '0', color: '#333', fontSize: '14px' }}>
                We opened a secure Razorpay checkout window. Follow the instructions there to complete
                your franchise fee payment. Please do not refresh or close this page until the payment is
                finished.
              </p>
              <div style={{ marginTop: '15px' }}>
                <div
                  className="spinner-small"
                  style={{
                    width: '24px',
                    height: '24px',
                    border: '3px solid rgba(12, 28, 45, 0.2)',
                    borderTopColor: '#0c1c2d',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto',
                  }}
                ></div>
                <p style={{ margin: '10px 0 0 0', fontSize: '12px', color: '#666' }}>
                  Awaiting confirmation from Razorpay...
                </p>
              </div>
            </div>
          )}

          {!agreement?.paymentCompleted && !existingPayment && (
            <button
              onClick={handlePayment}
              className="btn-payment"
              disabled={!agreedToTerms || processingPayment || !razorpayReady}
              style={{
                opacity: (!agreedToTerms || processingPayment || !razorpayReady) ? 0.6 : 1,
                cursor: (!agreedToTerms || processingPayment || !razorpayReady) ? 'not-allowed' : 'pointer'
              }}
            >
              {processingPayment ? (
                <>
                  <span>Processing...</span>
                  <div className="spinner-small" style={{
                    width: '20px',
                    height: '20px',
                    border: '2px solid rgba(12, 28, 45, 0.2)',
                    borderTopColor: '#0c1c2d',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                </>
              ) : (
                <>
                  <span>Pay Securely with Razorpay (Rs. {FRANCHISE_FEE.totalAmount.toLocaleString('en-IN')})</span>
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </>
              )}
            </button>
          )}

        </div>
      )}

      {/* Admin info message */}
      {isAdmin && (
        <div style={{
          margin: '2rem auto',
          padding: '1.5rem',
          background: 'rgba(212, 175, 55, 0.1)',
          border: '2px solid #d4af37',
          borderRadius: '12px',
          textAlign: 'center',
          maxWidth: '600px'
        }}>
          <p style={{ margin: 0, color: '#2c3e50', fontSize: '16px', fontWeight: '500' }}>
            You are viewing this agreement as an administrator.
          </p>
          <p style={{ margin: '8px 0 0 0', color: '#6c757d', fontSize: '14px' }}>
            Agreement Status: <strong style={{ color: agreement.status === 'pending_agreement' ? '#007bff' : '#28a745' }}>
              {agreement.status === 'pending_agreement' ? 'Pending User Signature' : 'Signed'}
            </strong>
          </p>
          {agreement.agreedToTerms && agreement.agreedAt && (
            <p style={{ margin: '4px 0 0 0', color: '#6c757d', fontSize: '14px' }}>
              Signed on: {new Date(agreement.agreedAt.toDate()).toLocaleDateString('en-IN', { 
                day: 'numeric', 
                month: 'long', 
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </p>
          )}
        </div>
      )}

      {showEditModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.6)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999,
          padding: '16px'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '0px',
            maxWidth: '760px',
            width: '100%',
            padding: '20px',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 12px 35px rgba(0,0,0,0.25)',
            border: '2px solid #0c1c2d',
            fontFamily: 'inherit'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div>
                <h3 style={{ margin: 0, color: '#0c1c2d', fontWeight: 800 }}>Edit Details</h3>
                <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px' }}>
                  Update your franchise details; changes reflect in the agreement before payment.
                </div>
              </div>
              <button
                onClick={handleCloseEdit}
                style={{
                  background: 'transparent',
                  border: '2px solid #0c1c2d',
                  color: '#0c1c2d',
                  width: '32px',
                  height: '32px',
                  borderRadius: '0px',
                  fontSize: '18px',
                  cursor: savingEdits ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700
                }}
                disabled={savingEdits}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              {[
                { label: 'Full Name', name: 'fullName', type: 'text' },
                { label: 'Email', name: 'email', type: 'text' },
                { label: 'Phone', name: 'phone', type: 'text' },
                { label: 'Entity Type', name: 'entityType', type: 'text' },
                { label: 'Preferred Location', name: 'preferredLocation', type: 'text' },
                {
                  label: 'Do You Hold Active RERA?',
                  name: 'holdActiveRera',
                  type: 'select',
                  options: [
                    { value: '', label: 'Select option' },
                    { value: 'yes', label: 'Yes' },
                    { value: 'no', label: 'No' },
                  ],
                },
                {
                  label: 'Can You Arrange For RERA in 90 Days?',
                  name: 'arrangeReraIn90Days',
                  type: 'select',
                  options: [
                    { value: '', label: 'Select option' },
                    { value: 'yes', label: 'Yes' },
                    { value: 'no', label: 'No' },
                  ],
                },
                { label: 'RERA Number', name: 'reraNumber', type: 'text' },
                { label: 'PAN Number', name: 'panNumber', type: 'text' },
                { label: 'GST Number', name: 'gstNumber', type: 'text' },
                {
                  label: 'Investment Budget Range',
                  name: 'investmentBudget',
                  type: 'select',
                  options: [
                    { value: '', label: 'Select budget range' },
                    { value: 'Rs 1-3 Lakhs', label: 'Rs 1-3 Lakhs' },
                    { value: 'Rs 3-5 Lakhs', label: 'Rs 3-5 Lakhs' },
                    { value: 'Rs 5-10 Lakhs', label: 'Rs 5-10 Lakhs' },
                    { value: 'Rs 10+ Lakhs', label: 'Rs 10+ Lakhs' },
                  ],
                },
                { label: 'Address Line 1', name: 'addressLine1', type: 'text' },
                { label: 'Address Line 2', name: 'addressLine2', type: 'text' },
                { label: 'City', name: 'city', type: 'text' },
                { label: 'State', name: 'state', type: 'text' },
                { label: 'Pincode', name: 'pincode', type: 'text' },
              ].map((field) => (
                <div key={field.name} style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '12px', color: '#0c1c2d', fontWeight: 600 }}>{field.label}</label>
                  {field.type === 'select' ? (
                    <select
                      name={field.name}
                      value={editForm?.[field.name] || ''}
                      onChange={handleEditChange}
                      style={{
                        padding: '10px 12px',
                        borderRadius: '0px',
                        border: '2px solid #0c1c2d',
                        fontSize: '14px',
                        background: '#f8f9fb',
                        color: '#0c1c2d',
                        fontFamily: 'inherit'
                      }}
                      disabled={
                        field.name === 'holdActiveRera'
                          ? editCanArrangeReraIn90Days
                          : field.name === 'arrangeReraIn90Days'
                            ? editHasActiveRera
                            : false
                      }
                    >
                      {(field.options || []).map((option) => (
                        <option key={`${field.name}-${option.value || 'blank'}`} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      name={field.name}
                      value={editForm?.[field.name] || ''}
                      onChange={handleEditChange}
                      disabled={field.name === 'reraNumber' ? !editHasActiveRera : false}
                      style={{
                        padding: '10px 12px',
                        borderRadius: '0px',
                        border: '2px solid #0c1c2d',
                        fontSize: '14px',
                        background:
                          field.name === 'reraNumber' && !editHasActiveRera ? '#eceff3' : '#f8f9fb',
                        color: '#0c1c2d'
                      }}
                    />
                  )}
                </div>
              ))}
            </div>

            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                onClick={handleCancelEdit}
                style={{
                  padding: '10px 16px',
                  borderRadius: '0px',
                  border: '1px solid #6c757d',
                  background: '#f8f9fa',
                  color: '#6c757d',
                  cursor: savingEdits ? 'not-allowed' : 'pointer'
                }}
                disabled={savingEdits}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdits}
                style={{
                  padding: '10px 16px',
                  borderRadius: '0px',
                  border: 'none',
                  background: savingEdits ? '#9ca3af' : '#0c1c2d',
                  color: 'white',
                  fontWeight: 700,
                  cursor: savingEdits ? 'not-allowed' : 'pointer'
                }}
                disabled={savingEdits}
              >
                {savingEdits ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const condition = authUser => !!authUser;

export default compose(
  withAuthorization(condition),
  withFirebase,
)(FranchiseAgreementBase);
